/**
 * The inbox filters (PRD-0075 R-B6): chips for the triage state and the rule, each with how many
 * alerts it keeps, and a choice of source. Every count is taken with the other filters applied,
 * so a chip says what clicking it shows. They sit above the inbox, so a filter that keeps nothing
 * can be changed back.
 *
 * Presentational: the feature keeps the values and counts the alerts it read.
 */
import { useTranslation } from 'react-i18next'
import { FilterChipGroup } from '@/components/ui/filter-chip'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { AlertState } from '@/services/api/case-monitor'
import type { MonitorRuleKind } from './AlertInbox'
import { ALERT_STATES } from './AlertTriage'
import { useRuleLabel } from './rule-name'

const FILTERS = 'cases.monitor.filters'
const ALL = 'all'

export interface InboxFilterValues {
  state: AlertState | null
  ruleId: string | null
  documentId: string | null
}

export const NO_INBOX_FILTERS: InboxFilterValues = { state: null, ruleId: null, documentId: null }

/** Where the inbox opens: the alerts nobody has triaged yet. */
export const DEFAULT_INBOX_FILTERS: InboxFilterValues = { ...NO_INBOX_FILTERS, state: 'new' }

/** How many alerts each choice keeps, the other filters applied. */
export interface InboxFacets {
  states: Readonly<Record<AlertState, number>>
  /** Every state, the other filters applied. */
  anyState: number
  rules: readonly { id: string; name: string | null; kind: MonitorRuleKind | null; count: number }[]
  anyRule: number
  sources: readonly { id: string; name: string; count: number }[]
}

export interface InboxFiltersProps {
  value: InboxFilterValues
  facets: InboxFacets
  onChange: (next: InboxFilterValues) => void
}

function StateChips({ value, facets, onChange }: InboxFiltersProps) {
  const { t } = useTranslation()
  return (
    <FilterChipGroup<AlertState>
      legend={t(`${FILTERS}.state`)}
      all={{ label: t(`${FILTERS}.allStates`), count: facets.anyState }}
      options={ALERT_STATES.map(state => ({
        value: state,
        label: t(`cases.monitor.triage.states.${state}`),
        count: facets.states[state],
      }))}
      value={value.state}
      onChange={state => onChange({ ...value, state })}
    />
  )
}

function RuleChips({ value, facets, onChange }: InboxFiltersProps) {
  const { t } = useTranslation()
  const nameOf = useRuleLabel()
  if (facets.rules.length < 2 && value.ruleId === null) return null
  return (
    <FilterChipGroup<string>
      legend={t(`${FILTERS}.rule`)}
      all={{ label: t(`${FILTERS}.allRules`), count: facets.anyRule }}
      options={facets.rules.map(rule => ({
        value: rule.id,
        label: nameOf(rule.name),
        count: rule.count,
      }))}
      value={value.ruleId}
      onChange={ruleId => onChange({ ...value, ruleId })}
    />
  )
}

function SourceSelect({ value, facets, onChange }: InboxFiltersProps) {
  const { t } = useTranslation()
  if (facets.sources.length < 2 && value.documentId === null) return null
  return (
    <Select
      value={value.documentId ?? ALL}
      onValueChange={next => onChange({ ...value, documentId: next === ALL ? null : next })}
    >
      <SelectTrigger size="sm" aria-label={t(`${FILTERS}.source`)} className="max-w-72 min-w-44">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{t(`${FILTERS}.allSources`)}</SelectItem>
        {facets.sources.map(source => (
          <SelectItem key={source.id} value={source.id}>
            {t(`${FILTERS}.sourceCount`, { name: source.name, count: source.count })}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export function InboxFilters(props: InboxFiltersProps) {
  const { t } = useTranslation()
  return (
    <div role="group" aria-label={t(`${FILTERS}.label`)} className="flex flex-col gap-2">
      <StateChips {...props} />
      <RuleChips {...props} />
      <SourceSelect {...props} />
    </div>
  )
}
